import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as R from 'ramda';

import Checkbox from '@app/ui/sports-components/Checkbox';
import { Button, Card, Row, Col, Flex, LoadingDots } from '@app/ui';
import AccordionContainer from '@app/ui/sports-components/AccordionContainer';
import List from '@app/ui/sports-components/AccordionList';
import Item from '@app/ui/sports-components/AccordionItem';
import Title from '@app/ui/sports-components/AccordionTitle';
import Icon from '@app/ui/sports-components/AccordionIcon';
import ChildItems from '@app/ui/sports-components/AccordionChildItems';
import classNames from '@app/ui/sports-components/utils/classNames';

import downloadCSV from '../utils/downloadCsv';
import connect from '../utils/connect';
import sportsApi from '../utils/sportsApi';
import { RELO_LOGO } from '../constants/config';
import { lightGray } from '../constants/colors';
import {
    getSponsorActive,
    getPlacementActive,
} from '../utils/getActiveOnboardingInstances';

import Tooltip from '@app/ui/sports-components/Tooltip';
import './viewConfig.scss';

const ViewConfig = ({ client, user: { csvDelimiter } }) => {
    const { t } = useTranslation('settings');
    const [isLoading, setLoading] = useState(true);
    const [rsps, setRsps] = useState(null);
    const [openRhId, setOpenRhId] = useState(null);
    const [isHidingAssets, setHideAssets] = useState(false);

    const fetchClientRSPs = async () => {
        try {
            const endpoint = `/clients/${client.id}/rsp-configuration`;
            const rspResponse = await sportsApi.url(endpoint).get().json();

            const orderedRsps = R.sortBy(
                R.path(['rightsholder', 'name']),
                rspResponse,
            ).map(rsp => {
                const { placements } = rsp;
                const activePlacements = placements
                    .filter(p => getPlacementActive(p))
                    .sort((a, b) => {
                        const nameA =
                            a.clientCustomPlacementName || a.placement?.name;
                        const nameB =
                            b.clientCustomPlacementName || b.placement?.name;
                        return nameA && nameB && nameA.localeCompare(nameB);
                    });
                const inactivePlacements = placements
                    .filter(p => !getPlacementActive(p))
                    .sort((a, b) => {
                        const nameA =
                            a.clientCustomPlacementName || a.placement?.name;
                        const nameB =
                            b.clientCustomPlacementName || b.placement?.name;
                        return nameA && nameB && nameA.localeCompare(nameB);
                    });
                const newPlacements = [
                    ...activePlacements,
                    ...inactivePlacements,
                ].map(pl => {
                    const { sponsors } = pl;
                    const activeSponsors = sponsors
                        .filter(s => getSponsorActive(s))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    const inactiveSponsors = sponsors
                        .filter(s => !getSponsorActive(s))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    const newSponsors = [
                        ...activeSponsors,
                        ...inactiveSponsors,
                    ];
                    return { ...pl, sponsors: newSponsors };
                });

                return { ...rsp, placements: newPlacements };
            });

            setRsps(orderedRsps);
            setLoading(false);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const downloadAsCsv = e => {
        e.preventDefault();
        const headers = [
            [
                t('settings:columns.client'),
                t('settings:columns.rightsholder'),
                t('settings:columns.placement'),
                t('settings:columns.sponsor'),
                t('settings:columns.active'),
            ],
        ];

        const data = R.sortBy(R.path(['rightsholder', 'name']), rsps).reduce(
            (rightsholderAcc, { rightsholder, placements }) => {
                const sortedPlacements = R.sortBy(
                    pl =>
                        pl.clientCustomPlacementName ||
                        R.path(['placement', 'name'], pl),
                    placements,
                );
                const values = sortedPlacements.reduce(
                    (
                        placementsAcc,
                        { placement, sponsors, clientCustomPlacementName },
                    ) => {
                        const placementDisplayName =
                            clientCustomPlacementName || placement.name;
                        const alphabeticallyOrderedSponsors = R.sortBy(
                            R.prop('name'),
                            sponsors,
                        );
                        const result = [
                            ...alphabeticallyOrderedSponsors
                                .filter(sp => getSponsorActive(sp))
                                .map(({ name: sponsorName }) => [
                                    client.name,
                                    rightsholder.name,
                                    placementDisplayName,
                                    sponsorName,
                                    true,
                                ]),
                            ...alphabeticallyOrderedSponsors
                                .filter(sp => !getSponsorActive(sp))
                                .map(({ name: sponsorName }) => [
                                    client.name,
                                    rightsholder.name,
                                    placementDisplayName,
                                    sponsorName,
                                    false,
                                ]),
                        ];

                        return [...placementsAcc, ...result];
                    },
                    [],
                );

                return [...rightsholderAcc, ...values];
            },
            headers,
        );

        const jsonToCsv = Papa.unparse(data, { delimiter: csvDelimiter });

        downloadCSV(jsonToCsv, `${client.name}-config`);
    };

    useEffect(() => {
        fetchClientRSPs();
    }, []);

    useEffect(() => {
        if (Array.isArray(rsps) && rsps.length === 1) {
            const {
                rightsholder: { id: rightholderId },
            } = rsps[0];
            setOpenRhId(rightholderId);
        }
    }, [rsps]);

    if (isLoading) return <LoadingDots size="lg" />;
    if (!Array.isArray(rsps) || !rsps.length)
        return <p>{t('clientConfiguration.onboarding.noRightsholder')}</p>;

    return (
        <Row gutter={16}>
            <Col
                md={24}
                className="gds-flex gds-flex--justify-between gds-flex--align-center -m-b-3 "
            >
                <div
                    className="gds-flex gds-flex--align-center gds-text--body-sm"
                    style={{
                        width: '100%',
                    }}
                >
                    <div className="gds-account__image -m-r-2">
                        <img
                            src={client.headerImageUrl || RELO_LOGO}
                            height="100%"
                            width="100%"
                        />
                    </div>
                    <div style={{ flex: ' 1 1 0%' }}>
                        <p className="gds-text--header-sm">{client.name}</p>
                    </div>
                    <>
                        <Checkbox
                            inline
                            label={
                                <>
                                    {t(
                                        'clientConfiguration.onboarding.hideInactiveAssets',
                                    )}
                                    <Tooltip
                                        text={t(
                                            'clientConfiguration.onboarding.hideInactiveAssetsTooltip',
                                        )}
                                        position="top-left"
                                    >
                                        <i className="fas fa-info-circle -m-h-1" />
                                    </Tooltip>
                                </>
                            }
                            size="sm"
                            style={{
                                float: 'right',
                            }}
                            checked={isHidingAssets}
                            onChange={() => {
                                setHideAssets(!isHidingAssets);
                            }}
                        />
                        <Button
                            size="small"
                            className="-m-l-2"
                            variant="primary"
                            onClick={downloadAsCsv}
                        >
                            {t('clientConfiguration.onboarding.exportCsv')}
                        </Button>
                    </>
                </div>
            </Col>
            <Col
                md={24}
                className="gds-flex gds-flex--justify-between gds-flex--align-center "
            >
                <AccordionContainer
                    className="-m-b-3"
                    style={{ width: '100%' }}
                >
                    <List>
                        {rsps.map((rsp, rhIndex) => {
                            const { rightsholder, placements } = rsp;
                            const {
                                mainLogo,
                                name: rightsholderName,
                                id: rightholderId,
                            } = rightsholder;
                            const rightsholderLogo =
                                !!mainLogo && mainLogo.url
                                    ? mainLogo.url
                                    : null;
                            const isOpen = rightholderId === openRhId;
                            const _toggle = () => {
                                if (!isOpen) {
                                    setOpenRhId(rightholderId);
                                } else {
                                    setOpenRhId(null);
                                }
                            };
                            return (
                                <Item
                                    isActive={isOpen}
                                    key={`column_accordion_${rhIndex}`}
                                >
                                    <Title
                                        context="secondary"
                                        onClick={_toggle}
                                        size="sm"
                                    >
                                        <div className="gds-flex gds-flex--justify-start gds-flex--align-center">
                                            <div className="gds-flex__item gds-flex__item--grow-0">
                                                <div
                                                    className="gds-account__image -m-r-2"
                                                    style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                    }}
                                                >
                                                    <img
                                                        src={rightsholderLogo}
                                                        height="100%"
                                                        width="100%"
                                                    />
                                                </div>
                                            </div>
                                            <div className="decorate decorate--all gds-flex__item gds-flex__item--grow-0">
                                                {rightsholderName}
                                            </div>
                                        </div>
                                    </Title>
                                    <Icon
                                        isWhite
                                        size="sm"
                                        className="--cursor-pointer"
                                        onClick={_toggle}
                                    />
                                    <ChildItems
                                        size="sm"
                                        className="-color-bg-white"
                                        style={{
                                            minHeight: isOpen ? '200px' : '0',
                                            paddingBottom: isOpen ? '1em' : '0',
                                            margin: 0,
                                        }}
                                    >
                                        {placements.map(
                                            (pls, plsIndex, totalPls) => {
                                                const {
                                                    placement,
                                                    sponsors,
                                                    clientCustomPlacementName,
                                                } = pls;
                                                const {
                                                    name: placementName,
                                                    examples,
                                                } = placement;
                                                const displayPlacementName =
                                                    clientCustomPlacementName ||
                                                    placementName;
                                                const orderedSponsors = [
                                                    ...sponsors.filter(sp =>
                                                        getSponsorActive(sp),
                                                    ),
                                                    ...sponsors.filter(
                                                        sp =>
                                                            !getSponsorActive(
                                                                sp,
                                                            ),
                                                    ),
                                                ];

                                                if (
                                                    isHidingAssets &&
                                                    !getPlacementActive(
                                                        placement,
                                                    )
                                                )
                                                    return;
                                                const maxImagesPerRow = 8;

                                                const exampleRows =
                                                    examples.length
                                                        ? examples.reduce(
                                                              (acc, ex) => {
                                                                  if (
                                                                      acc[
                                                                          acc.length -
                                                                              1
                                                                      ].length <
                                                                      maxImagesPerRow
                                                                  ) {
                                                                      acc[
                                                                          acc.length -
                                                                              1
                                                                      ].push(
                                                                          ex,
                                                                      );
                                                                  } else {
                                                                      acc.push([
                                                                          ex,
                                                                      ]);
                                                                  }
                                                                  return acc;
                                                              },
                                                              [[]],
                                                          )
                                                        : [];

                                                const isLast =
                                                    plsIndex ==
                                                    totalPls.length - 1;
                                                const isFirst = plsIndex == 0;
                                                return (
                                                    <Fragment
                                                        key={`placement_container_${plsIndex}`}
                                                    >
                                                        <Col
                                                            md={24}
                                                            className={
                                                                isFirst
                                                                    ? `-m-t-3`
                                                                    : ''
                                                            }
                                                        >
                                                            <h4 className="gds-card__title gds-text--regular -m-b-1 -p-b-1">
                                                                <span className="gds-number-circle gds-number-circle--sm">
                                                                    {plsIndex +
                                                                        1}
                                                                </span>
                                                                <span className="gds-text--bold gds-text--secondary">
                                                                    {
                                                                        displayPlacementName
                                                                    }
                                                                </span>

                                                                {!getPlacementActive(
                                                                    placement,
                                                                ) && (
                                                                    <span
                                                                        className="gds-badge -m-l-3 p-a-1"
                                                                        style={{
                                                                            height: '1.5rem',
                                                                            lineHeight:
                                                                                '1.5rem',
                                                                            fontSize:
                                                                                '0.8rem',
                                                                            verticalAlign:
                                                                                'middle',
                                                                            backgroundColor:
                                                                                lightGray,
                                                                            borderRadius:
                                                                                '3px',
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="gds-text"
                                                                            style={{
                                                                                fontWeight:
                                                                                    'bold',
                                                                                color: '#46536c',
                                                                            }}
                                                                        >
                                                                            {t(
                                                                                'clientConfiguration.onboarding.inactive',
                                                                            )}
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </h4>
                                                        </Col>
                                                        <Col
                                                            md={24}
                                                            className="-m-t-2"
                                                        >
                                                            <Flex
                                                                vertical
                                                                className="gds-layout__container"
                                                            >
                                                                <Row
                                                                    gutter={16}
                                                                >
                                                                    {orderedSponsors.map(
                                                                        (
                                                                            sp,
                                                                            spIndex,
                                                                        ) => {
                                                                            const {
                                                                                name: sponsorName,
                                                                                logos: sponsorLogos,
                                                                            } =
                                                                                sp;

                                                                            const sponsorImageClassnames =
                                                                                classNames(
                                                                                    'gds-sponsors__image',
                                                                                    {
                                                                                        '-color-bg-white':
                                                                                            getSponsorActive(
                                                                                                sp,
                                                                                            ),
                                                                                        'grayed-background':
                                                                                            !getSponsorActive(
                                                                                                sp,
                                                                                            ),
                                                                                    },
                                                                                );

                                                                            const imgClassnames =
                                                                                classNames(
                                                                                    {
                                                                                        'grayed-image':
                                                                                            !getSponsorActive(
                                                                                                sp,
                                                                                            ),
                                                                                    },
                                                                                );

                                                                            if (
                                                                                isHidingAssets &&
                                                                                !getSponsorActive(
                                                                                    sp,
                                                                                )
                                                                            )
                                                                                return;
                                                                            return (
                                                                                <Col
                                                                                    key={`sponsor_card_${spIndex}`}
                                                                                    md={
                                                                                        4
                                                                                    }
                                                                                >
                                                                                    <Card
                                                                                        className="-p-v-2"
                                                                                        style={{
                                                                                            marginBottom:
                                                                                                '0.75rem',
                                                                                            backgroundColor:
                                                                                                !getSponsorActive(
                                                                                                    sp,
                                                                                                )
                                                                                                    ? lightGray
                                                                                                    : null,
                                                                                        }}
                                                                                        styles={{
                                                                                            body: {
                                                                                                padding: 0,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <Flex
                                                                                            vertical
                                                                                            className="gds-layout__container"
                                                                                        >
                                                                                            <Row
                                                                                                gutter={
                                                                                                    16
                                                                                                }
                                                                                            >
                                                                                                <Col
                                                                                                    md={
                                                                                                        24
                                                                                                    }
                                                                                                >
                                                                                                    <Tooltip
                                                                                                        text={
                                                                                                            sponsorName
                                                                                                        }
                                                                                                        position="top"
                                                                                                        className="sponsor-tooltip"
                                                                                                    >
                                                                                                        <p className="gds-text--body-sm gds-text--bold -ellipsis">
                                                                                                            {
                                                                                                                sponsorName
                                                                                                            }
                                                                                                        </p>
                                                                                                    </Tooltip>
                                                                                                </Col>
                                                                                                {sponsorLogos &&
                                                                                                    sponsorLogos.map(
                                                                                                        (
                                                                                                            splogo,
                                                                                                            logoIndex,
                                                                                                            spLogos,
                                                                                                        ) => {
                                                                                                            const {
                                                                                                                url: logo,
                                                                                                            } =
                                                                                                                splogo;

                                                                                                            const showLeftOvers =
                                                                                                                spLogos.length >
                                                                                                                    3 &&
                                                                                                                logoIndex ==
                                                                                                                    2;
                                                                                                            const leftOvers =
                                                                                                                spLogos.length -
                                                                                                                2;
                                                                                                            const noShow =
                                                                                                                logoIndex >
                                                                                                                2;

                                                                                                            return (
                                                                                                                <Fragment
                                                                                                                    key={`sponsor_logos_${spIndex}_${logoIndex}`}
                                                                                                                >
                                                                                                                    {!noShow && (
                                                                                                                        <Col
                                                                                                                            md={
                                                                                                                                8
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <div className="decorate decorate--all gds-flex__item gds-flex__item--grow-0">
                                                                                                                                <div
                                                                                                                                    className={
                                                                                                                                        sponsorImageClassnames
                                                                                                                                    }
                                                                                                                                >
                                                                                                                                    {!showLeftOvers && (
                                                                                                                                        <img
                                                                                                                                            src={
                                                                                                                                                logo
                                                                                                                                            }
                                                                                                                                            height="100%"
                                                                                                                                            width="100%"
                                                                                                                                            className={
                                                                                                                                                imgClassnames
                                                                                                                                            }
                                                                                                                                        />
                                                                                                                                    )}
                                                                                                                                    {showLeftOvers && (
                                                                                                                                        <div className="gds-leftover-number  gds-text--body-md gds-text--regular -p-t-1">
                                                                                                                                            +
                                                                                                                                            {
                                                                                                                                                leftOvers
                                                                                                                                            }
                                                                                                                                        </div>
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            </div>
                                                                                                                        </Col>
                                                                                                                    )}
                                                                                                                </Fragment>
                                                                                                            );
                                                                                                        },
                                                                                                    )}
                                                                                                {!sponsorLogos && (
                                                                                                    <Fragment>
                                                                                                        <Col
                                                                                                            md={
                                                                                                                8
                                                                                                            }
                                                                                                        >
                                                                                                            <div className="decorate decorate--all gds-flex__item gds-flex__item--grow-0">
                                                                                                                <div
                                                                                                                    className="-color-bg-white"
                                                                                                                    style={{
                                                                                                                        width: '2.5rem',
                                                                                                                        height: '2.5rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    <div className="gds-text--body-md gds-text--regular -p-t-1" />
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </Col>
                                                                                                    </Fragment>
                                                                                                )}
                                                                                            </Row>
                                                                                        </Flex>
                                                                                    </Card>
                                                                                </Col>
                                                                            );
                                                                        },
                                                                    )}
                                                                </Row>
                                                            </Flex>
                                                        </Col>
                                                        <Col
                                                            md={24}
                                                            className="-m-t-2"
                                                        >
                                                            <Flex
                                                                vertical
                                                                className="gds-layout__container"
                                                            >
                                                                <Row
                                                                    gutter={16}
                                                                >
                                                                    {exampleRows.length >
                                                                        0 &&
                                                                        exampleRows.map(
                                                                            (
                                                                                exRow,
                                                                                exRowIndex,
                                                                            ) => {
                                                                                return (
                                                                                    <Col
                                                                                        md={
                                                                                            24
                                                                                        }
                                                                                        key={`example_row_${placement.id}_${exRowIndex}`}
                                                                                    >
                                                                                        <div className="gds-flex gds-flex--wrap gds-ecosystem-images -m-b-1">
                                                                                            {exRow.map(
                                                                                                (
                                                                                                    example,
                                                                                                    exIndex,
                                                                                                ) => {
                                                                                                    const {
                                                                                                        imageUrl,
                                                                                                    } =
                                                                                                        example;
                                                                                                    return (
                                                                                                        <div
                                                                                                            key={`example_container_${plsIndex}_${exIndex}`}
                                                                                                            className="decorate gds-flex__item gds-flex__item--grow-0"
                                                                                                        >
                                                                                                            <div className="gds-ranker-card__item">
                                                                                                                <img
                                                                                                                    className="gds-image"
                                                                                                                    src={
                                                                                                                        imageUrl
                                                                                                                    }
                                                                                                                    style={{
                                                                                                                        objectFit:
                                                                                                                            'contain',
                                                                                                                    }}
                                                                                                                />
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    );
                                                                                                },
                                                                                            )}
                                                                                        </div>
                                                                                    </Col>
                                                                                );
                                                                            },
                                                                        )}
                                                                </Row>
                                                            </Flex>
                                                        </Col>
                                                        {!isLast && (
                                                            <div className="gds-divider">
                                                                <span className="gds-divider__line" />
                                                            </div>
                                                        )}
                                                    </Fragment>
                                                );
                                            },
                                        )}
                                    </ChildItems>
                                </Item>
                            );
                        })}
                    </List>
                </AccordionContainer>
            </Col>
        </Row>
    );
};

ViewConfig.propTypes = {
    client: PropTypes.object.isRequired,
    user: PropTypes.object,
};

export default connect(['client', 'user', 'settings'])(ViewConfig);
